import React from 'react';
import { Spinner } from 'react-bootstrap';

import {
  LomavisTableCell1,
  LomavisTableCellAlign1
} from '@/lomavisSharedUi/atoms/table/LomavisTableCell1';

export type LomavisDataTableRow1 = Record<string, React.ReactNode> & {
  id?: React.Key;
};

export type LomavisDataTableColumn1 = {
  key: string;
  header: React.ReactNode;
  align?: LomavisTableCellAlign1;
  className?: string;
  headerClassName?: string;
  numeric?: boolean;
  nowrap?: boolean;
  render?: (row: LomavisDataTableRow1, rowIndex: number) => React.ReactNode;
  width?: number | string;
};

export interface LomavisDataTable1Props {
  actions?: React.ReactNode;
  ariaLabel?: string;
  bordered?: boolean;
  caption?: string;
  className?: string;
  columns: LomavisDataTableColumn1[];
  compact?: boolean;
  emptyLabel?: string;
  getRowKey?: (row: LomavisDataTableRow1, rowIndex: number) => React.Key;
  hover?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  minWidth?: number | string;
  onRowClick?: (row: LomavisDataTableRow1, rowIndex: number) => void;
  rowBorder?: 'solid' | 'dashed';
  rowClassName?: (row: LomavisDataTableRow1, rowIndex: number) => string;
  rows: LomavisDataTableRow1[];
  subtitle?: React.ReactNode;
  tableClassName?: string;
  title?: React.ReactNode;
}

export const LomavisDataTable1: React.FC<LomavisDataTable1Props> = ({
  actions,
  ariaLabel,
  bordered = false,
  caption,
  className = '',
  columns,
  compact = false,
  emptyLabel = 'No records found',
  getRowKey,
  hover = true,
  loading = false,
  loadingLabel = 'Loading...',
  minWidth = 720,
  onRowClick,
  rowBorder = 'solid',
  rowClassName,
  rows,
  subtitle,
  tableClassName = '',
  title
}) => {
  const getCellClassName = (
    columnClassName: string | undefined,
    columnIndex: number
  ) =>
    [
      compact ? 'px-3 py-2' : 'px-4 py-3',
      columnIndex === 0 ? (compact ? 'ps-3' : 'ps-4') : '',
      columnIndex === columns.length - 1 ? (compact ? 'pe-3' : 'pe-4') : '',
      columnClassName
    ]
      .filter(Boolean)
      .join(' ');

  const tableClasses = [
    'table mb-0 small',
    hover ? 'table-hover' : '',
    bordered ? 'table-bordered' : '',
    compact ? 'table-sm' : '',
    rowBorder === 'dashed' ? 'border-dashed' : '',
    tableClassName
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`overflow-hidden rounded-4 border bg-white ${className}`}>
      {(title || subtitle || actions) && (
        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 border-bottom px-3 py-3 px-md-4">
          <div>
            {title && <h3 className="m-0 fs-6 fw-bold text-body">{title}</h3>}
            {subtitle && (
              <div className="mt-1 text-muted small fw-semibold">
                {subtitle}
              </div>
            )}
          </div>
          {actions && (
            <div className="d-inline-flex flex-shrink-0 align-items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      )}

      <div className="table-responsive">
        <table
          className={tableClasses}
          aria-label={ariaLabel}
          style={{ minWidth }}
        >
          {caption && (
            <caption className="px-3 py-2 text-start text-muted small fw-semibold">
              {caption}
            </caption>
          )}
          <thead className="table-light border-bottom">
            <tr>
              {columns.map((column, columnIndex) => (
                <LomavisTableCell1
                  key={column.key}
                  as="th"
                  align={column.align}
                  className={getCellClassName(
                    column.headerClassName,
                    columnIndex
                  )}
                  numeric={column.numeric}
                  nowrap={column.nowrap}
                  scope="col"
                  style={{ width: column.width }}
                >
                  {column.header}
                </LomavisTableCell1>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <LomavisTableCell1
                  align="center"
                  className="px-3 py-5"
                  colSpan={columns.length}
                  variant="muted"
                >
                  <span className="d-inline-flex align-items-center justify-content-center gap-2">
                    <Spinner animation="border" role="status" size="sm">
                      <span className="visually-hidden">{loadingLabel}</span>
                    </Spinner>
                    <span>{loadingLabel}</span>
                  </span>
                </LomavisTableCell1>
              </tr>
            ) : rows.length > 0 ? (
              rows.map((row, rowIndex) => (
                <tr
                  key={getRowKey?.(row, rowIndex) ?? row.id ?? rowIndex}
                  className={`${onRowClick ? 'cursor-pointer' : ''} ${
                    rowClassName?.(row, rowIndex) ?? ''
                  }`}
                  onClick={() => onRowClick?.(row, rowIndex)}
                >
                  {columns.map((column, columnIndex) => (
                    <LomavisTableCell1
                      key={column.key}
                      align={column.align}
                      className={getCellClassName(
                        column.className,
                        columnIndex
                      )}
                      numeric={column.numeric}
                      nowrap={column.nowrap}
                      variant={column.numeric ? 'strong' : 'body'}
                    >
                      {column.render
                        ? column.render(row, rowIndex)
                        : row[column.key]}
                    </LomavisTableCell1>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <LomavisTableCell1
                  align="center"
                  className="px-3 py-5"
                  colSpan={columns.length}
                  variant="muted"
                >
                  {emptyLabel}
                </LomavisTableCell1>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
